import React from 'react';
import AttachmentList from './AttachmentList';
import OfficialBadge from './OfficialBadge';
import PdfButton from './PdfButton';
import ShareLinkButton from './ShareLinkButton';
import { useI18n } from './i18n';
import { SHEET_DETAIL, OIL_POSITIVES, coffeeResult, oilResult } from './productScoring';
import { ABTM_ELEMENTS, ABTM_GROUPS, ABTM_MAX_TOTAL, abtmResult } from './abtmSheet';

const UNITS = { leaf_grams: ' g', water_ml: ' ml', water_temperature: ' °C', steep_seconds: ' s' };
const FORMAL = { coffee: true, oil: true, tea: false, vinegar: false };

// Detail view of a sheet of the Products tab (any of the four categories), driven by SHEET_DETAIL.
// Props: category, sheet, owner (bool), onBack, onEdit, onDelete. The PDF is generated by the API from the saved sheet.
function ProductSheetDetail({ category, sheet, owner, onBack, onEdit, onDelete }) {
  const { t, tc, formatDate, formatNumber } = useI18n();
  const config = SHEET_DETAIL[category];
  const value = (name, max) => {
    const v = sheet[name];
    if (v === null || v === undefined || v === '') return '—';
    const unit = name === 'declared_age_years' ? ` ${t('products.unit.years')}` : (UNITS[name] || '');
    return `${formatNumber(v, 2)}${max ? `/${max}` : ''}${unit}`;
  };

  const abtm = category === 'vinegar' && sheet.protocol === 'abtm_dop';
  const abtmTotal = abtm ? (sheet.abtm_total ?? abtmResult(sheet).total) : null;
  const powder = category === 'tea' && sheet.gbt_category === 'powder';
  const bag = category === 'tea' && sheet.gbt_category === 'tea_bag';
  const fieldLabel = (name) => t(bag && (name === 'dry_leaf' || name === 'infused_leaf') ? `products.field_bag.${name}` : `products.field.${name}`);
  const coffee = category === 'coffee' ? coffeeResult(sheet) : null;
  const oil = category === 'oil' ? oilResult(sheet) : null;
  const total = category === 'coffee' ? (sheet.total_score ?? coffee.total) : sheet.total_score;
  const qualityClass = category === 'coffee' ? (sheet.quality_class || coffee.qualityClass) : null;

  return (
    <div className="detail">
      <button className="back-btn" onClick={onBack}>{t('ui.back_archive')}</button>
      <ShareLinkButton type={category} id={sheet.id} visibility={sheet.visibility} isOwner={owner} />
      <h2>{sheet.product_name} {sheet.harvest_year || ''}</h2>
      <OfficialBadge status={FORMAL[category] || abtm ? 'formal' : 'unofficial'}
        source={t(abtm ? 'products.source.vinegar_abtm' : `products.source.${category}`)}
        sourceNote={t(abtm ? 'products.source_note.vinegar_abtm' : `products.source_note.${category}`)} />
      <dl className="product-summary">
        {sheet.producer && <div><dt>{t('products.f.producer')}</dt><dd>{sheet.producer}</dd></div>}
        {sheet.origin && <div><dt>{t('products.f.origin')}</dt><dd>{sheet.origin}</dd></div>}
        {sheet.variety && <div><dt>{t(`products.f.variety.${category}`)}</dt><dd>{sheet.variety}</dd></div>}
        {sheet.sample_code && <div><dt>{t('products.f.sample_code')}</dt><dd>{sheet.sample_code}</dd></div>}
        {sheet.event && <div><dt>{t('products.field.event')}</dt><dd>{sheet.event}</dd></div>}
        {sheet.table_label && <div><dt>{t('products.field.table_label')}</dt><dd>{sheet.table_label}</dd></div>}
        {config.codes.map(([field, prefix]) => (sheet[field] ? (
          <div key={field}><dt>{t(`products.field.${field}`)}</dt><dd>{tc(`products.${prefix}`, sheet[field])}</dd></div>
        ) : null))}
        {category === 'oil' && sheet.serving_temperature != null && (
          <div><dt>{t('products.field.serving_temperature')}</dt><dd>{formatNumber(sheet.serving_temperature)} °C</dd></div>
        )}
      </dl>
      <p><strong>{t('app.d.taster')}:</strong> {sheet.taster || '—'} | <strong>{t('app.d.date')}:</strong> {formatDate(sheet.tasting_date) || '—'}</p>
      {sheet.venue && <p><strong>{t('app.d.venue')}:</strong> {sheet.venue}</p>}

      {abtm && ABTM_GROUPS.map((group) => (
        <div key={group}>
          <h3>{t(`vinegar.abtm.group.${group}`)}</h3>
          <div className="detail-grid">
            {ABTM_ELEMENTS.filter((el) => el.group === group).map((el) => {
              const level = Number(sheet[el.field]);
              const chosen = level >= 1 && level <= 5;
              const text = chosen ? `${el.descriptors ? `${el.descriptors[level - 1]} · ` : ''}${el.points[level - 1]}/${el.points[0]}` : '—';
              return <span key={el.field}>{t(`vinegar.abtm.el.${el.field}`)}: {text}</span>;
            })}
          </div>
        </div>
      ))}

      {!abtm && config.groups.map(([group, rows]) => (
        <div key={group}>
          <h3>{t(`products.group.${category}.${group}`)}</h3>
          <div className="detail-grid">
            {rows.filter(([name]) => !(powder && name === 'infused_leaf')).map(([name, max]) => (
              <span key={name}>{fieldLabel(name)}: {value(name, max)}</span>
            ))}
            {category === 'oil' && group === 'defects' && sheet.defect_other_name && (
              <span>{t('oil.f.defect_other_name')}: {tc('oil.other_defect', sheet.defect_other_name)}</span>
            )}
          </div>
        </div>
      ))}

      {(category !== 'vinegar' || abtm) && (
      <div className="live-result">
        {abtm && <span className="live-total">{abtmTotal != null ? `${formatNumber(abtmTotal, 0)}/${ABTM_MAX_TOTAL}` : '—'}</span>}
        {category === 'tea' && sheet.weighted_score != null && (
          <span className="live-total">{formatNumber(sheet.weighted_score, 1)}/100</span>
        )}
        {(category === 'coffee' || (category === 'tea' && sheet.weighted_score == null)) && (
          <span className="live-total">{total != null ? `${formatNumber(total, 2)}/${category === 'coffee' ? 100 : config.total}` : '—'}</span>
        )}
        {qualityClass && <span className={`pairing-badge-sm ${qualityClass.replace(/_/g, '-')}`}>{tc('coffee.class', qualityClass)}</span>}
        {category === 'coffee' && sheet.defects_points != null && <span>{t('coffee.res.defects')}: <strong>−{sheet.defects_points}</strong></span>}
        {oil && (
          <>
            <span className="live-total">{tc('oil.category', sheet.category_eu || oil.categoryEu)}</span>
            <span>{t('oil.res.max_defect')}: <strong>{formatNumber(sheet.max_defect ?? oil.maxDefect, 1)}</strong></span>
            <span>{t('oil.res.ioc')}: <strong>{tc('oil.category', sheet.category_ioc || oil.categoryIoc)}</strong></span>
            {OIL_POSITIVES.map((name) => <span key={name}>{t(`oil.p.${name}`)}: <strong>{tc('oil.band', oil.bands[name])}</strong></span>)}
            {(sheet.mild ?? oil.mild) && <span className="chip selected">{t('oil.res.mild')}</span>}
            {(sheet.well_balanced ?? oil.wellBalanced) && <span className="chip selected">{t('oil.res.well_balanced')}</span>}
            <small>{t('oil.res.indicative')}</small>
          </>
        )}
      </div>
      )}

      {sheet.notes && <p style={{ marginTop: 12 }}><strong>{t('app.d.notes')}:</strong> {sheet.notes}</p>}

      <p><PdfButton path={`${config.path}/${sheet.id}/pdf`} fallbackName={`${config.path}-sheet.pdf`} /></p>

      <AttachmentList sheetId={sheet.id} canEdit={owner} />

      {owner && (
        <>
          <button className="submit-btn" style={{ marginRight: 8 }} onClick={onEdit}>{t('ui.edit')}</button>
          <button className="delete-btn" onClick={onDelete}>{t('app.d.delete_sheet')}</button>
        </>
      )}
    </div>
  );
}

export default ProductSheetDetail;
